import React, { useState } from 'react';
import { Box, Container, TextField, Button, Typography, Stack, Alert, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import AnimatedSection from '../../components/AnimatedSection';
import '../../glow-effect.css';

const EMAILJS_CONFIG = {
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
  TO_EMAIL: process.env.REACT_APP_EMAILJS_TO_EMAIL || 'contact@peoplefortech.com',
};

const SectionContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: 'transparent',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: '#000000',
  [theme.breakpoints.down('sm')]: { fontSize: '1.5rem' },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  color: '#666666',
}));

const ContactCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  padding: theme.spacing(5),
  width: '100%',
  maxWidth: '600px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
  },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3) },
}));

const ContactSectionV2 = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [consulta, setConsulta] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (!EMAILJS_CONFIG.PUBLIC_KEY?.trim() || !EMAILJS_CONFIG.SERVICE_ID?.trim() || !EMAILJS_CONFIG.TEMPLATE_ID?.trim()) {
      setMessage({ type: 'error', text: 'Error de configuración. Contacta al administrador.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const templateParams = {
        to_email: EMAILJS_CONFIG.TO_EMAIL,
        from_email: email,
        message: `Contacto v2 - Email: ${email}\n\nConsulta:\n${consulta}`,
        subject: 'Contacto v2 - PeopleForTech',
      };
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
      await emailjs.send(EMAILJS_CONFIG.SERVICE_ID, EMAILJS_CONFIG.TEMPLATE_ID, templateParams);
      setMessage({ type: 'success', text: t('contact.success') });
      setEmail('');
      setConsulta('');
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      setMessage({ type: 'error', text: t('contact.error') || 'Error al enviar. Intenta de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection direction="bottom" delay={0.3}>
      <SectionContainer component="section">
        <Stack spacing={3} alignItems="center">
          <ContactCard>
            <Box sx={{ mb: 3 }}>
              <Title variant="h3">{t('contact.title')}</Title>
              <Subtitle variant="body1">{t('contact.subtitle')}</Subtitle>
            </Box>
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={t('contact.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('contact.emailPlaceholder')}
                variant="outlined"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: 'black' },
                    '&.Mui-focused fieldset': { borderColor: 'black' },
                  },
                }}
              />
              <TextField
                fullWidth
                label={t('contact.consultaLabel')}
                placeholder={t('contact.consultaPlaceholder')}
                value={consulta}
                onChange={(e) => setConsulta(e.target.value)}
                variant="outlined"
                multiline
                minRows={6}
                maxRows={12}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: 'black' },
                    '&.Mui-focused fieldset': { borderColor: 'black' },
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', fontSize: '0.95rem' }}>
                {t('contact.responsePromise')}
              </Typography>
              {message.text && (
                <Alert severity={message.type === 'success' ? 'success' : 'error'}>{message.text}</Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                className="glow-on-hover"
                sx={{
                  py: 1.5,
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: 10,
                  '&:hover': { backgroundColor: '#333333' },
                  '&:disabled': { backgroundColor: '#666666' },
                }}
              >
                {isSubmitting ? t('contact.sending') : t('contact.submit')}
              </Button>
            </Box>
          </ContactCard>
        </Stack>
      </SectionContainer>
    </AnimatedSection>
  );
};

export default ContactSectionV2;
